"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useOnboarding } from "@/contexts/OnboardingContext";
import { recordNearMeFunnelMetric } from "@/lib/nearMeMetricsClient";
import { MARKET_NEAR_ME_ENTRYPOINT_TARGETS } from "@/lib/push-notifications/near-me-notification-metrics";
import { MARKET_COPY } from "@/lib/market/copy";

type MarketIntroBannerProps = {
  includeAlertsLine?: boolean;
};

export default function MarketIntroBanner({
  includeAlertsLine = false,
}: MarketIntroBannerProps) {
  const { isPromptOpen, completePrompt } = useOnboarding();
  const visible = isPromptOpen("marketIntro");
  const impressionLogged = useRef(false);
  const [expanded, setExpanded] = useState(false);

  const dismiss = useCallback(() => {
    completePrompt("marketIntro");
  }, [completePrompt]);

  useEffect(() => {
    if (!visible || impressionLogged.current) {
      return;
    }
    impressionLogged.current = true;
    void recordNearMeFunnelMetric("near_me_entrypoint_impression", {
      source: "market",
      target: MARKET_NEAR_ME_ENTRYPOINT_TARGETS.welcomeBanner,
    });
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      className="mb-4 rounded-lg border border-border bg-secondary px-4 py-3 text-sm text-foreground"
      role="region"
      aria-label="Marketplace tips"
    >
      <div className="flex gap-3">
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-text-light">
              {MARKET_COPY.introBannerCollapsed}
              <button
                type="button"
                onClick={() => setExpanded((prev) => !prev)}
                className="font-medium text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
                aria-expanded={expanded}
              >
                {" " + MARKET_COPY.introBannerTipsLabel}
              </button>
            </span>
          </div>
          {expanded ? (
            <ul className="list-disc list-inside space-y-1 text-text-light text-xs sm:text-sm">
              <li>
                Use <strong className="text-foreground">Search</strong> for
                items (e.g. fridge, bed).
              </li>
              <li>
                Narrow results with{" "}
                <strong className="text-foreground">category</strong> and{" "}
                <strong className="text-foreground">location</strong> filters.
              </li>
              {includeAlertsLine ? (
                <li>
                  Get alerts for new listings near you — open{" "}
                  <Link
                    href="/profile?tab=notifications&nearMeSource=market"
                    className="font-medium text-primary hover:underline"
                    onClick={() => {
                      void recordNearMeFunnelMetric(
                        "near_me_entrypoint_click",
                        {
                          source: "market",
                          target:
                            MARKET_NEAR_ME_ENTRYPOINT_TARGETS.alertSettingsLink,
                        },
                      );
                    }}
                  >
                    Alert settings
                  </Link>
                  .
                </li>
              ) : null}
            </ul>
          ) : null}
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="shrink-0 rounded-md p-1 text-text-light hover:bg-surface/80 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          aria-label="Dismiss marketplace tips"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
